import React, { useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import inventoryApi from '../../../../services/inventoryApi';
import SearchablePagedDropdown from '../../../Common/SearchablePagedDropdown';
import LoadingSpinner from '../../../Common/LoadingSpinner';
import { getSlimLogoPath } from '../../../../utils/slimLogo';

// ─── Formatting ───────────────────────────────────────────────────────────────
export const money = (n) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
export const qty = (n) => (Number(n) || 0).toLocaleString('en-US', { maximumFractionDigits: 4 });
export const today = () => new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 10);
export const monthStart = () => `${today().slice(0, 8)}01`;
export const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }) : '—');
export const esc = (v) => String(v ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const labelCls = 'text-xs font-semibold text-gray-500 uppercase tracking-wide';
const inputCls = 'px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm bg-gray-50 hover:bg-white transition-colors';

export const Field = ({ label, children, className = '' }) => (
  <div className={`flex flex-col gap-1 ${className}`}>
    <label className={labelCls}>{label}</label>
    {children}
  </div>
);

export const DateInput = ({ value, onChange }) => (
  <input type="date" value={value} onChange={(e) => onChange(e.target.value)} className={`${inputCls} min-w-[150px]`} />
);

// ─── Warehouse / location ────────────────────────────────────────────────────
export const WarehouseSelect = ({ value, onChange }) => {
  const [list, setList] = useState([]);
  useEffect(() => {
    inventoryApi.getWarehouses({ limit: 1000 })
      .then(res => { const d = res?.data?.data || res?.data || res || []; setList(Array.isArray(d) ? d : []); })
      .catch(() => setList([]));
  }, []);
  return (
    <select value={value} onChange={(e) => onChange(e.target.value, e.target.selectedOptions[0]?.text || '')} className={`${inputCls} min-w-[180px]`}>
      <option value="">All locations</option>
      {list.map(w => <option key={w._id} value={w._id}>{w.name || w.locationName || w.code}</option>)}
    </select>
  );
};

// ─── Product Group → Product Type → Product ──────────────────────────────────
// Group = top-level category, Type = a sub-category of the group, Product = an item.
export const EMPTY_PRODUCT = { productGroup: '', productType: '', product: '', groupLabel: '', typeLabel: '', productLabel: '' };

export const ProductFilters = ({ value, onChange }) => {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    inventoryApi.getCategories({ limit: 1000 })
      .then(res => setCats(res?.data || []))
      .catch(() => setCats([]));
  }, []);
  const groups = cats.filter(c => !c.parent);
  const types = cats.filter(c => c.parent && String(c.parent?._id || c.parent) === String(value.productGroup));
  const set = (patch) => onChange({ ...value, ...patch });
  const itemsPage = async (search, page, limit) => {
    const res = await inventoryApi.getItems({ search: search || undefined, page, limit, ...(value.productType ? { category: value.productType } : {}) });
    const pag = res?.pagination || {};
    return { items: res?.data || [], page: pag.page || page, pages: pag.pages || 1, total: pag.total || 0 };
  };
  return (
    <>
      <Field label="Product Group">
        <select value={value.productGroup} onChange={(e) => set({ productGroup: e.target.value, groupLabel: e.target.value ? e.target.selectedOptions[0].text : '', productType: '', typeLabel: '', product: '', productLabel: '' })} className={`${inputCls} min-w-[170px]`}>
          <option value="">All groups</option>
          {groups.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Product Type">
        <select value={value.productType} disabled={!value.productGroup || !types.length} onChange={(e) => set({ productType: e.target.value, typeLabel: e.target.value ? e.target.selectedOptions[0].text : '', product: '', productLabel: '' })} className={`${inputCls} min-w-[170px] disabled:opacity-50`}>
          <option value="">{value.productGroup && !types.length ? 'No types' : 'All types'}</option>
          {types.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="Product" className="min-w-[220px]">
        <div className="[&>div>button]:h-[38px]! [&>div>button]:rounded-lg! [&>div>button]:bg-gray-50!">
          <SearchablePagedDropdown
            value={value.product}
            displayLabel={value.productLabel}
            onChange={(id, label) => set({ product: id || '', productLabel: label || '' })}
            fetchPage={itemsPage}
            resetKey={value.productType || ''}
            getLabel={(i) => [i.sku, i.name].filter(Boolean).join(' — ')}
            noun="item"
            placeholder="All products"
            searchPlaceholder="Search code or name…"
          />
        </div>
      </Field>
    </>
  );
};

export const productParams = (p) => ({
  ...(p.productGroup ? { productGroup: p.productGroup } : {}),
  ...(p.productType ? { productType: p.productType } : {}),
  ...(p.product ? { product: p.product } : {}),
});

export const productFilterLine = (p) => [
  p.groupLabel && `Group: ${p.groupLabel}`, p.typeLabel && `Type: ${p.typeLabel}`, p.productLabel && `Product: ${p.productLabel}`,
].filter(Boolean).join(' · ');

// ─── Page shell ──────────────────────────────────────────────────────────────
export const ReportShell = ({ title, subtitle, icon: Icon, filters, onGenerate, onReset, loading, actions, children, empty }) => (
  <div className="flex flex-col h-[calc(100vh-64px)]">
    <div className="flex-shrink-0 px-4 sm:px-6 lg:px-8 pt-4">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="bg-gradient-to-r from-indigo-600 to-indigo-500 px-5 py-3 flex items-center gap-3 rounded-t-xl">
          {Icon && <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center flex-shrink-0"><Icon className="w-4 h-4 text-white" /></div>}
          <div>
            <h2 className="text-sm font-semibold text-white">{title}</h2>
            <p className="text-indigo-200 text-xs">{subtitle}</p>
          </div>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onGenerate(); }}>
          <div className="px-5 py-4 flex flex-wrap items-end gap-4">
            {filters}
            <div className="flex-1" />
            <div className="flex items-center gap-2">
              <button type="button" onClick={onReset} className="px-4 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Reset</button>
              <button type="submit" disabled={loading} className="px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:bg-indigo-300 shadow-sm">
                {loading ? 'Generating…' : 'Generate Report'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
    {loading ? (
      <div className="flex-grow flex items-center justify-center"><LoadingSpinner size="lg" fullScreen={false} /></div>
    ) : children ? (
      <div className="flex-grow overflow-auto px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        {actions && <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-4 py-3 flex flex-wrap justify-between items-center gap-3">{actions}</div>}
        {children}
      </div>
    ) : (
      <div className="flex-grow flex items-center justify-center px-4">
        <div className="text-center">
          {Icon && <Icon className="mx-auto h-14 w-14 text-gray-300 mb-3" />}
          <h3 className="text-base font-medium text-gray-900">No report generated</h3>
          <p className="mt-1 text-sm text-gray-500">{empty || 'Choose the filters and click "Generate Report"'}</p>
        </div>
      </div>
    )}
  </div>
);

export const ExportButtons = ({ onExcel, onPrint }) => (
  <div className="flex gap-2">
    <button type="button" onClick={onExcel} className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700">Export Excel</button>
    <button type="button" onClick={onPrint} className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700">Print PDF</button>
  </div>
);

export const ViewToggle = ({ value, onChange, options }) => (
  <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden">
    {options.map(o => (
      <button key={o.value} type="button" onClick={() => onChange(o.value)}
        className={`px-3 py-1.5 text-xs font-medium ${value === o.value ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
        {o.label}
      </button>
    ))}
  </div>
);

// ─── Print / PDF ─────────────────────────────────────────────────────────────
// tableHtml: a full <table>…</table>; rows use classes: group (section header), sub (subtotal), total.
export const printReport = ({ title, subtitle, filtersLine, tableHtml, landscape = false, note = '' }) => {
  try {
    const logoUrl = window.location.origin + getSlimLogoPath();
    const generatedAt = new Date().toLocaleString('en-US', { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: true });
    const html = `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8"/><title>${esc(title)}</title><style>
      *{box-sizing:border-box;margin:0;padding:0}*{-webkit-print-color-adjust:exact !important;print-color-adjust:exact !important}
      body{font-family:Arial,sans-serif;font-size:10px;color:#111;padding:28px 30px}
      #toolbar{position:fixed;top:0;left:0;right:0;background:#374151;padding:8px 16px;display:flex;gap:10px;align-items:center}
      #toolbar span{color:#fff;font-size:13px;font-weight:600;flex:1}#toolbar button{padding:6px 16px;border:none;border-radius:4px;font-weight:600;cursor:pointer}
      .sp{height:44px}@page{size:A4 ${landscape ? 'landscape' : 'portrait'};margin:0}@media print{#toolbar,.sp{display:none}body{padding:12mm 12mm 14mm}tr{page-break-inside:avoid}}
      .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #374151;padding-bottom:10px;margin-bottom:10px}
      .head img{height:44px}.t{font-size:18px;font-weight:800;color:#374151;text-align:right}.s{font-size:10px;color:#6b7280;text-align:right;margin-top:2px}
      table{width:100%;border-collapse:collapse}th{background:#4b5563;color:#f3f4f6;font-size:8px;text-transform:uppercase;letter-spacing:.04em;padding:5px 6px;text-align:left;border:1px solid #4b5563}
      td{padding:4px 6px;border-bottom:1px solid #e5e7eb;font-size:9px;vertical-align:top}.r{text-align:right;font-family:monospace}
      tr.group td{background:#eef2ff;font-weight:700;color:#3730a3}tr.sub td{background:#f3f4f6;font-weight:700}tr.total td{background:#e5e7eb;font-weight:800;border-top:2px solid #374151}
      tr.child td{color:#4b5563;font-size:8px}.note{margin-top:8px;font-size:8px;color:#6b7280}.foot{text-align:center;font-size:8px;color:#9ca3af;margin-top:14px}
    </style></head><body>
      <div id="toolbar"><span>${esc(title)}</span><button onclick="window.print()">Download PDF</button><button onclick="window.close()">Close</button></div><div class="sp"></div>
      <div class="head"><img src="${logoUrl}" alt="Logo"/><div><div class="t">${esc(title)}</div><div class="s">${esc(subtitle || '')}</div>${filtersLine ? `<div class="s">${esc(filtersLine)}</div>` : ''}<div class="s">${generatedAt}</div></div></div>
      ${tableHtml}${note ? `<div class="note">${esc(note)}</div>` : ''}<div class="foot">This document is system generated — ${generatedAt}</div>
    </body></html>`;
    const url = URL.createObjectURL(new Blob([html], { type: 'text/html; charset=utf-8' }));
    const win = window.open(url, '_blank');
    if (!win) { toast.error('Pop-up blocked! Please allow pop-ups for this site to print reports.'); URL.revokeObjectURL(url); return; }
    win.onload = () => setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    console.error('Print error:', err);
    toast.error('Failed to print report. Please try again.');
  }
};

// Runs a report call with toast errors (backend message first)
export const runReport = async (fn, setLoading, setData) => {
  setLoading(true);
  try {
    const res = await fn();
    if (res?.success === false) { toast.error(res.message || 'Failed to generate report'); return; }
    setData(res?.data ?? res);
  } catch (err) {
    toast.error(err.response?.data?.message || 'Failed to generate report');
  } finally {
    setLoading(false);
  }
};
